import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useMissions } from "@/hooks/useMissions";
import { useAuth } from "@/hooks/useAuth";
import { MISSIONS } from "@/data/missions";

export function MissionsEngine() {
  const [location] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { activeId, progressMap, completeMission, setActive } = useMissions();
  const completingRef = useRef<Set<string>>(new Set());

  function tryComplete(missionId: string, options?: { silent?: boolean }) {
    const progress = progressMap[missionId];
    if (progress?.status === "completed") return;
    if (completingRef.current.has(missionId)) return;
    const mission = MISSIONS.find((m) => m.id === missionId);
    if (!mission) return;

    completingRef.current.add(missionId);
    completeMission(mission);
    if (activeId === missionId) setActive(null);

    if (!options?.silent) {
      toast({
        title: `+${mission.points} XP`,
        description: `Mission complete: ${mission.title}${mission.realReward ? " — real-world reward unlocked! Check your email." : ""}`,
      });
    }
  }

  // Auto-complete missions whose precondition is already true based on
  // the user's account state. Avoids the awkward "you're already a
  // seller, but please toggle to seller first" loop the testers hit.
  // Silent so testers aren't spammed with toasts on every login.
  useEffect(() => {
    if (!user) return;
    const roles = String((user as any).roles || "").toLowerCase();
    if (roles === "seller" || roles === "both") {
      tryComplete("S01", { silent: true });
    }
    if (roles === "both") {
      tryComplete("P02", { silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!activeId) return;
    const mission = MISSIONS.find((m) => m.id === activeId);
    if (!mission) return;
    if (mission.detect.kind !== "route") return;
    if (mission.detect.pattern.test(location)) {
      tryComplete(mission.id);
    }
  }, [location, activeId]);

  useEffect(() => {
    if (!activeId) return;
    const mission = MISSIONS.find((m) => m.id === activeId);
    if (!mission) return;
    if (mission.detect.kind !== "mutation") return;

    function handler(ev: Event) {
      if (!mission || mission.detect.kind !== "mutation") return;
      const detail = (ev as CustomEvent).detail as
        | { method: string; url: string; status: number }
        | undefined;
      if (!detail) return;
      if (detail.status >= 400) return;
      if (detail.method !== mission.detect.method.toUpperCase()) return;
      if (!mission.detect.urlPattern.test(detail.url)) return;
      tryComplete(mission.id);
    }
    window.addEventListener("marketlink:api", handler as EventListener);
    return () => window.removeEventListener("marketlink:api", handler as EventListener);
  }, [activeId]);

  useEffect(() => {
    if (!activeId) return;
    const mission = MISSIONS.find((m) => m.id === activeId);
    if (!mission) return;
    if (mission.detect.kind !== "click") return;

    function handler(ev: MouseEvent) {
      if (!mission || mission.detect.kind !== "click") return;
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(mission.detect.selector)) {
        tryComplete(mission.id);
      }
    }
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [activeId]);

  useEffect(() => {
    if (!activeId) return;
    const mission = MISSIONS.find((m) => m.id === activeId);
    if (!mission) return;
    if (mission.detect.kind !== "event") return;
    const eventName = mission.detect.name;

    function handler() {
      tryComplete(mission!.id);
    }
    window.addEventListener(eventName, handler);
    window.dispatchEvent(
      new CustomEvent("marketlink:mission-recheck", { detail: { event: eventName } }),
    );
    return () => window.removeEventListener(eventName, handler);
  }, [activeId]);

  return null;
}
